import { z } from 'zod';
// Cross-channel budget proposal (the autopilot roadmap (CommitMind) A7).
//
// Highest-stakes autopilot decision — Mag NEVER moves money
// autonomously here. She reads the past N days of paid signal
// across meta + google, identifies where the dollar is converting
// better, and emits a proposal feed card the founder approves or
// dismisses. A0 is intentionally unreachable for this decision
// type per the roadmap.
//
// The proposal carries a typed plan (per-deliverable adjustments)
// so "apply" is a button click, not a free-text instruction:
// founder presses, server walks the adjustments, server calls the
// per-platform budget mutation APIs. Without a typed plan the
// proposal would be advice — with it, mag proposes AND has staged
// the change behind a single click.
/** Per-deliverable budget adjustment within a proposal.
 *
 *  `kind` keeps the discriminated union narrow so apply-time
 *  routing can dispatch on channel without re-parsing the
 *  deliverable id. */
export const crossChannelBudgetAdjustmentSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('meta_paid_ad'),
        /** Magig campaign uuid (creative_ads). Apply walks the
         *  campaign's draft.deliverables to find the deliverable +
         *  call the meta publisher. */
        campaignId: z.string().uuid(),
        /** Deliverable id within the campaign's plan (e.g.
         *  "paid-angle-1"). String, not uuid — deliverables live in
         *  jsonb. */
        deliverableId: z.string().min(1).max(64),
        /** Current daily budget (USD whole dollars) at proposal time.
         *  Used by the renderer to show "$25 → $15" diffs and by apply
         *  to detect concurrent founder edits (mismatch = bail out). */
        currentDailyBudgetUsd: z.number().int().min(1).max(10_000),
        /** Proposed daily budget (USD whole dollars). */
        newDailyBudgetUsd: z.number().int().min(1).max(10_000),
        /** Short mag-voice line on WHY this ad's budget moved.
         *  Renders inline next to the diff. */
        why: z.string().min(1).max(280),
    }),
    z.object({
        kind: z.literal('google_search_ad'),
        campaignId: z.string().uuid(),
        deliverableId: z.string().min(1).max(64),
        currentDailyBudgetUsd: z.number().int().min(1).max(10_000),
        newDailyBudgetUsd: z.number().int().min(1).max(10_000),
        why: z.string().min(1).max(280),
    }),
]);
/** Lifecycle state on the card.
 *
 *  open → founder hasn't decided.
 *  applied → founder clicked apply + the orchestrator ran (audit
 *    log row written; per-deliverable applyOutcomes populated).
 *  dismissed → founder explicitly dropped the proposal.
 *  expired → proposal lived past its freshness window without
 *    action; next scheduler tick produces a new one.
 */
export const crossChannelBudgetProposalStateSchema = z.enum([
    'open',
    'applied',
    'dismissed',
    'expired',
]);
/** Per-adjustment apply result. Populated when state flips to
 *  'applied'. Each entry mirrors one of `adjustments[]` by
 *  (campaignId, deliverableId). */
export const crossChannelBudgetApplyOutcomeSchema = z.object({
    campaignId: z.string().uuid(),
    deliverableId: z.string().min(1).max(64),
    /** 'ok' — platform mutation succeeded.
     *  'skipped_concurrent_edit' — current_daily_budget on the
     *  deliverable no longer matches what the proposal saw.
     *  'skipped_not_launched' — deliverable was launched_at = null,
     *  so there's no platform budget to mutate.
     *  'failed' — platform API rejected the change (rate limit,
     *  account-level constraint, etc.). detail carries the
     *  platform error string. */
    outcome: z.enum(['ok', 'skipped_concurrent_edit', 'skipped_not_launched', 'failed']),
    detail: z.string().optional(),
});
/** The full feed card payload. */
export const crossChannelBudgetProposalPayloadSchema = z.object({
    kind: z.literal('cross_channel_budget_proposal'),
    /** Per-brand-per-week dedup key (YYYY-MM-DD, brand-tz Monday).
     *  Mirrors the autopilot weekly recap convention. */
    weekStart: z.string(),
    /** End of the signal window (inclusive Sunday). */
    weekEnd: z.string(),
    /** Lifecycle state — see schema above. */
    state: crossChannelBudgetProposalStateSchema,
    /** Total dollars proposed to shift across channels. Always
     *  positive — `direction` carries the sign. 0 when mag's
     *  recommendation is to hold. */
    shiftAmountUsd: z.number().min(0).max(100_000),
    /** Direction of the recommended shift. 'hold' means signal
     *  isn't strong enough either way — mag still emits the card so
     *  the founder sees the read, but adjustments will be empty. */
    direction: z.enum(['meta_to_google', 'google_to_meta', 'hold']),
    /** Mag-voice one-sentence headline. Title of the card.
     *  ("google is converting 2× better than meta this week — i'd
     *  shift $300 over for next week.") */
    summary: z.string().min(1).max(280),
    /** Longer mag-voice rationale. Receipts: which channel
     *  performed how, why she's confident in the shift size, what
     *  she's NOT recommending and why. Renders below the summary
     *  on the card. */
    rationale: z.string().min(1).max(2000),
    /** Per-deliverable adjustments. Empty when direction='hold'. */
    adjustments: z.array(crossChannelBudgetAdjustmentSchema).max(20),
    /** Confidence 0..10. Drives renderer copy ("low conviction —
     *  i'd watch another week first" at ≤3, "high conviction" at
     *  ≥7). */
    confidence: z.number().int().min(0).max(10),
    /** Pre-formatted per-channel rollup mag used to build the
     *  proposal. Renders as a quiet "↘ what i looked at" block so
     *  the founder can audit her reasoning. */
    signalReadout: z.object({
        metaTotalSpendUsd: z.number().min(0),
        metaAvgCtrPercent: z.number().min(0).max(100),
        metaAvgConversionRate: z.number().min(0).max(1).optional(),
        metaAvgCostPerConversionUsd: z.number().min(0).optional(),
        metaAdsRunning: z.number().int().min(0),
        googleTotalSpendUsd: z.number().min(0),
        googleAvgCtrPercent: z.number().min(0).max(100),
        googleAvgConversionRate: z.number().min(0).max(1),
        googleAvgCostPerConversionUsd: z.number().min(0),
        googleCampaignsRunning: z.number().int().min(0),
        windowDays: z.number().int().min(1).max(60),
    }),
    /** Apply outcomes — populated when state='applied'. */
    applyOutcomes: z.array(crossChannelBudgetApplyOutcomeSchema).optional(),
    /** When state flipped to applied/dismissed. ISO 8601. */
    decidedAt: z.string().datetime().optional(),
    /** LLM cost for the proposer agent call. Stored for the
     *  per-campaign cost rollup the activity meter reads. */
    costUsd: z.number().min(0).optional(),
});
//# sourceMappingURL=cross-channel-budget.js.map